import { useState } from "react"
import {
  MessageSquare,
  Send,
  Plus,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  Truck,
  FileText,
  Users,
  ChevronDown,
} from "lucide-react"
import {
  useSmsAccounts,
  useSmsMessages,
  useSendSms,
  useSmsTemplates,
  useSmsTemplateUse,
  type SmsMessage,
  type SmsTemplate,
} from "@/hooks/use-sms"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import { formatDate } from "@/lib/utils"

// ── Status Config ───────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  pending: { label: "Oczekuje", color: "#F59E0B", icon: Clock },
  queued: { label: "W kolejce", color: "#3B82F6", icon: Clock },
  sent: { label: "Wysłano", color: "#3B82F6", icon: Send },
  delivered: { label: "Dostarczono", color: "#10B981", icon: CheckCircle2 },
  failed: { label: "Błąd", color: "#EF4444", icon: XCircle },
}

const STATUS_FILTERS = [
  { key: "", label: "Wszystkie" },
  { key: "sent", label: "Wysłane" },
  { key: "delivered", label: "Dostarczono" },
  { key: "pending", label: "Oczekuje" },
  { key: "failed", label: "Błąd" },
]

// ── SMS Template Selector ───────────────────────────────────────

function SmsTemplateSelector({
  onSelect,
}: {
  onSelect: (template: SmsTemplate) => void
}) {
  const [open, setOpen] = useState(false)
  const { data: templatesData } = useSmsTemplates()
  const templates = templatesData?.data || []
  const templateUseMutation = useSmsTemplateUse()

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 text-[11px] hover:underline text-primary"
      >
        <FileText size={12} />
        Szablon
      </button>
    )
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(false)}
        className="flex items-center gap-1 text-[11px] hover:underline text-primary"
      >
        <FileText size={12} />
        Szablon
        <ChevronDown size={10} />
      </button>
      <div
        className="absolute left-0 bottom-6 z-50 w-64 rounded-md border shadow-lg bg-card"
      >
        <div className="max-h-48 overflow-auto">
          {templates.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              Brak szablonów SMS
            </p>
          ) : (
            templates.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  onSelect(t)
                  templateUseMutation.mutate(t.id)
                  setOpen(false)
                }}
                className="flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors hover:opacity-80 text-foreground"
              >
                <span className="flex items-center gap-2 text-xs font-medium">
                  {t.name}
                  {t.category && (
                    <span
                      className="rounded px-1 py-0.5 text-[9px] bg-primary/10 text-primary"
                    >
                      {t.category}
                    </span>
                  )}
                </span>
                <span className="truncate text-[10px] text-muted-foreground">
                  {t.body.slice(0, 80)}...
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ── Send SMS Dialog ─────────────────────────────────────────────

function SendSmsDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data: accountsData } = useSmsAccounts()
  const accounts = accountsData?.data || []
  const sendMutation = useSendSms()

  const [accountId, setAccountId] = useState("")
  const [toNumber, setToNumber] = useState("")
  const [body, setBody] = useState("")

  const charCount = body.length
  const partsCount = charCount <= 160 ? 1 : Math.ceil(charCount / 153)
  const charsLeft = partsCount === 1 ? 160 - charCount : 153 - (charCount % 153 || 153)

  const handleSend = async () => {
    const acctId = accountId || accounts[0]?.id
    if (!acctId || !toNumber || !body) return

    try {
      await sendMutation.mutateAsync({
        sms_account_id: acctId,
        to_number: toNumber,
        body,
      })
      toast.success("SMS wysłany")
      setToNumber("")
      setBody("")
      onOpenChange(false)
    } catch {
      toast.error("Błąd wysyłania SMS")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Wyślij SMS</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Account */}
          {accounts.length > 1 && (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Konto SMS
              </label>
              <select
                value={accountId || accounts[0]?.id || ""}
                onChange={(e) => setAccountId(e.target.value)}
                className="w-full rounded border bg-muted px-2.5 py-1.5 text-sm text-foreground"
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.sender_name || a.provider})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* To number */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Numer telefonu *
            </label>
            <input
              value={toNumber}
              onChange={(e) => setToNumber(e.target.value)}
              className="w-full rounded border bg-muted px-2.5 py-1.5 text-sm text-foreground"
              placeholder="+48 123 456 789"
            />
          </div>

          {/* Body */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Treść wiadomości *
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full min-h-[100px] rounded border bg-muted px-2.5 py-1.5 text-sm text-foreground"
              placeholder="Wpisz treść SMS..."
            />
            <div className="mt-1 flex items-center justify-between">
              <SmsTemplateSelector
                onSelect={(t) => setBody(t.body)}
              />
              <span
                className={`text-[10px] ${charCount > 160 ? "text-amber-500" : "text-muted-foreground"}`}
              >
                {charCount}/160 znaków · {partsCount} {partsCount === 1 ? "część" : partsCount < 5 ? "części" : "części"}
                {charsLeft > 0 && ` · ${charsLeft} zost.`}
              </span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} size="sm">
            Anuluj
          </Button>
          <Button
            onClick={handleSend}
            disabled={sendMutation.isPending || !toNumber || !body || accounts.length === 0}
            size="sm"
            className="gap-1.5"
          >
            <Send size={13} />
            {sendMutation.isPending ? "Wysyłanie..." : "Wyślij"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── SMS Message Row ─────────────────────────────────────────────

function SmsMessageRow({ message }: { message: SmsMessage }) {
  const config = STATUS_CONFIG[message.status] || STATUS_CONFIG.pending
  const StatusIcon = config.icon

  return (
    <div
      className="flex items-start gap-3 border-b px-4 py-3"
    >
      <div
        className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: `${config.color}15` }}
      >
        <StatusIcon size={14} style={{ color: config.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-foreground">
            {message.to_number}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge
              variant="secondary"
              className="text-[10px]"
              style={{ color: config.color }}
            >
              {config.label}
            </Badge>
            <span className="text-[10px] text-muted-foreground">
              {formatDate(message.created_at)}
            </span>
          </div>
        </div>
        <p
          className="mt-0.5 text-xs text-muted-foreground"
        >
          {message.body}
        </p>
        {message.error_message && (
          <p className="mt-0.5 text-[10px] text-red-500">
            {message.error_message}
          </p>
        )}
        <div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground">
          {message.parts_count > 1 && <span>{message.parts_count} części</span>}
          {message.sent_at && <span>Wysłano: {formatDate(message.sent_at)}</span>}
          {message.delivered_at && <span>Dostarcz.: {formatDate(message.delivered_at)}</span>}
        </div>
      </div>
    </div>
  )
}

// ── Main Page ───────────────────────────────────────────────────

export function SmsPage() {
  const [statusFilter, setStatusFilter] = useState("")
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const { data: messagesData, isLoading } = useSmsMessages(
    statusFilter ? { status: statusFilter } : {}
  )
  const messages = messagesData?.data || []
  const { data: accountsData } = useSmsAccounts()
  const accounts = accountsData?.data || []

  return (
    <div className="mx-auto max-w-4xl p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Wiadomości SMS
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Wysyłaj i śledź wiadomości SMS
          </p>
        </div>
        <Button
          onClick={() => setSendDialogOpen(true)}
          size="sm"
          className="gap-1.5"
          disabled={accounts.length === 0}
        >
          <Plus size={14} />
          Wyślij SMS
        </Button>
      </div>

      {/* No account warning */}
      {accounts.length === 0 && !isLoading && (
        <div
          className="mb-4 rounded-lg border border-dashed border-amber-500 bg-amber-500/5 p-4 text-center"
        >
          <p className="text-sm text-foreground">
            Brak skonfigurowanego konta SMS
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Skonfiguruj konto SMS w Ustawieniach, aby móc wysyłać wiadomości
          </p>
        </div>
      )}

      {/* Status filter */}
      <div className="mb-4 flex gap-1">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${statusFilter === f.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Messages list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      ) : messages.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12"
        >
          <MessageSquare size={40} className="text-muted-foreground" />
          <p className="mt-3 text-sm font-medium text-foreground">
            Brak wiadomości SMS
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {accounts.length > 0
              ? "Wyślij pierwszą wiadomość SMS"
              : "Najpierw skonfiguruj konto SMS w ustawieniach"}
          </p>
        </div>
      ) : (
        <div
          className="surface-card overflow-hidden"
        >
          {messages.map((msg) => (
            <SmsMessageRow key={msg.id} message={msg} />
          ))}
        </div>
      )}

      {/* Send dialog */}
      <SendSmsDialog
        open={sendDialogOpen}
        onOpenChange={setSendDialogOpen}
      />
    </div>
  )
}
